import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MultiSelect } from "react-multi-select-component";
import { useDispatch, useSelector } from "react-redux";
import {
  addProjects,
  getAllEmployees,
  getProjects,
  updateProjects,
} from "../redux/actionCreator";
import {
  all_employees,
  project_data,
  project_loading,
} from "../redux/commonReducer";
import { getSession } from "../utils/session";
import {
  Plus, Search, AppWindow, Terminal, Folder, Rocket, Server, Globe, X,
  MoreHorizontal, ExternalLink, Copy as CopyIcon, FileText, Pencil, Users,
} from "lucide-react";
import ServersPanel from "./ServersPanel";
// ApisEditor + legacy web_url / admin_url removed from the create form —
// per-stage `urls[]` covers everything. Old projects with these fields
// keep loading fine (still in the schema, returned by the API).
import "./Employees/employeesList.scss";
import "./projects.scss";

// URLs are now per-stage. The single `web_url` / `admin_url` fields on
// the schema stay for legacy reads but the form drives `urls[]` —
// { label, url, stage } entries. Default to an empty list so creating
// a project doesn't force the operator to fill anything they don't have.
const PROJECT_STAGES = [
  { value: "development", label: "Development", color: "#2563eb", bg: "#dbeafe" },
  { value: "staging",     label: "Staging",     color: "#b45309", bg: "#fef3c7" },
  { value: "production",  label: "Production",  color: "#b91c1c", bg: "#fee2e2" },
];

// `web_url`, `admin_url`, `apis` removed from the form — per-stage `urls[]`
// (Development / Staging / Production) covers everything. Schema-level
// fields are still accepted by the backend so old projects don't break.
const blankProject = {
  id: null,
  project_name: "",
  urls: [],
  members: [],
  servers: [],
};

const blankFilter = {
  start_date: "",
  end_date: "",
  keyword: "",
  page: 0,
};

const Projects = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const projects = useSelector(project_data) || [];
  const allEmployees = useSelector(all_employees) || [];
  const loading = useSelector(project_loading);
  const role = getSession()?.role;
  const isSuperAdmin = role === "super_admin";
  // Admin includes super_admin — used to gate the Servers panel access
  // (which exposes connection metadata and the management UI). Regular
  // employees still see Terminal / Files / Deploy inline on the card
  // so they can do their job without the Servers screen.
  const isAdmin = isSuperAdmin || role === "admin";

  const [showModal, setShowModal] = useState(false);
  const [err, setErr] = useState({});
  const [data, setData] = useState(blankProject);
  const [filters, setFilters] = useState(blankFilter);

  useEffect(() => {
    dispatch(getAllEmployees({ page: 0, limit: 200 }));
    runSearch(blankFilter);
  }, []);

  const runSearch = (f) => dispatch(getProjects(f));

  const handleClose = () => {
    setShowModal(false);
    setData(blankProject);
    setErr({});
  };

  const onChange = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErr((e) => ({ ...e, [key]: "" }));
  };

  const onFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const clearFilters = () => {
    setFilters(blankFilter);
    runSearch(blankFilter);
  };

  const validate = () => {
    const t = {};
    if (!data.project_name?.trim()) t.project_name = "Required";
    if (!data.members?.length) t.members = "Pick at least one member";
    // Per-stage URLs are OPTIONAL at create time — operators often onboard
    // a project before staging/prod environments exist. Validate that
    // each entered row has BOTH label and URL together.
    for (const u of (data.urls || [])) {
      const hasL = !!u.label?.trim(), hasU = !!u.url?.trim();
      if (hasL !== hasU) { t.urls = "Every URL needs both a label and a URL (or remove the row)"; break; }
    }
    setErr(t);
    return Object.keys(t).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    const urls = (data.urls || [])
      .filter((u) => u.label?.trim() && u.url?.trim())
      .map((u) => ({
        ...(u._id ? { _id: u._id } : {}),
        label: u.label.trim(),
        url: u.url.trim(),
        stage: u.stage || "development",
        is_public: !!u.is_public,
      }));
    const payload = {
      project_name: data.project_name.trim(),
      urls,
      members: data.members.map((m) => m._id),
      servers: data.servers || [],
    };
    if (data.id) {
      dispatch(
        updateProjects({ ...payload, id: data.id }, (ok) => {
          if (ok) {
            handleClose();
            runSearch(filters);
          }
        })
      );
    } else {
      dispatch(
        addProjects(payload, (ok) => {
          if (ok) {
            handleClose();
            runSearch(filters);
          }
        })
      );
    }
  };

  const openCreate = () => {
    setData(blankProject);
    setErr({});
    setShowModal(true);
  };

  const openEdit = (proj) => {
    // Prefer the new `apis` array. Fall back to legacy single `api_url`
    // so old projects don't show an empty list in the editor.
    const urlsFromDoc = Array.isArray(proj.urls)
      ? proj.urls.map((u) => ({
          _id: u._id, label: u.label || "", url: u.url || "",
          stage: u.stage || "development",
          is_public: !!u.is_public,
        }))
      : [];
    setData({
      id: proj._id,
      project_name: proj.project_name || "",
      urls: urlsFromDoc,
      members: allEmployees
        .filter((e) =>
          (proj.members || []).map(String).includes(String(e._id))
        )
        .map((el) => ({
          label:
            [el.firstname, el.lastname].filter(Boolean).join(" ") ||
            el.name ||
            el.email,
          value: el._id,
          _id: el._id,
        })),
      // Servers come back from the list endpoint without secrets (the API
      // strips encrypted_* and surfaces a `has_sudo_password` flag instead).
      // Spread everything so the Edit-server form gets every toggle and path
      // back; ServersPanel treats blank secret on a row with an _id as
      // "keep current".
      servers: (proj.servers || []).map((s) => ({ ...s })),
    });
    setShowModal(true);
  };

  const openWebsite = (proj) => {
    // No token in the URL — the shared cookie + nginx auth_request on the
    // jumphost authenticate every sub-app request automatically.
    window.open(proj.web_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="projects-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Projects</h2>
          <small className="page-sub">
            Web apps gated by the security console.
          </small>
        </div>
        {isAdmin && (
          <div className="page-actions">
            <Button className="btn-primary-solid" onClick={openCreate}>
              <Plus size={16} className="me-1" /> Add Project
            </Button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="filters-bar">
          <DatePicker
            className="form-control"
            placeholderText="Start date"
            selected={filters.start_date || null}
            onChange={(date) => onFilter("start_date", date)}
            dateFormat="dd MMM yyyy"
            maxDate={new Date()}
          />
          <DatePicker
            className="form-control"
            placeholderText="End date"
            selected={filters.end_date || null}
            minDate={filters.start_date || undefined}
            onChange={(date) => onFilter("end_date", date)}
            dateFormat="dd MMM yyyy"
            maxDate={new Date()}
          />
          <div className="search-box flex-grow-1">
            <span className="search-icon"><Search size={16} /></span>
            <input
              className="form-control"
              placeholder="Search by project or member name"
              value={filters.keyword}
              onChange={(e) => onFilter("keyword", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch(filters)}
            />
          </div>
          <Button
            className="btn-primary-solid"
            onClick={() => runSearch(filters)}
          >
            Search
          </Button>
          <Button variant="light" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      )}

      {loading ? (
        <div className="dash-panel emp-panel">
          <div className="empty-state">
            <Spinner animation="border" />
          </div>
        </div>
      ) : projects.length === 0 ? (
        <div className="dash-panel emp-panel">
          <div className="empty-state">
            <div className="empty-art"><Folder size={40} strokeWidth={1.2} /></div>
            <h4>No projects yet</h4>
            <p>
              {isAdmin
                ? "Add your first project — assign members and they'll see it on their dashboard."
                : "You have no projects assigned. Contact your administrator."}
            </p>
            {isAdmin && (
              <Button className="btn-primary-solid" onClick={openCreate}>
                ＋ Add Project
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="project-grid">
          {projects.map((proj) => (
            <ProjectCard
              key={proj._id}
              proj={proj}
              isSuperAdmin={isSuperAdmin}
              isAdmin={isAdmin}
              onEdit={() => openEdit(proj)}
              onView={() => openWebsite(proj)}
              onLogs={() => navigate("/logs")}
              onServers={() => navigate(`/projects/${proj._id}/servers`)}
              onDeploy={() => navigate(`/deploy/${proj._id}`)}
            />
          ))}
        </div>
      )}

      <Modal
        show={showModal}
        onHide={handleClose}
        size="lg"
        centered
        dialogClassName="sh-form-modal"
      >
        <Modal.Header closeButton>
          <span className="sh-modal-icon"><AppWindow size={22} /></span>
          <div className="sh-modal-titles">
            <h2>{data.id ? "Edit Project" : "Add Project"}</h2>
            <span className="sh-modal-sub">
              Web app gated by the security console
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="form-section">
            <h6 className="form-section-title">Project details</h6>
            <div className="row">
              <div className="col-md-12">
                <Form.Group className="mb-0">
                  <Form.Label>Project name *</Form.Label>
                  <Form.Control
                    value={data.project_name}
                    onChange={(e) => onChange("project_name", e.target.value)}
                    placeholder="e.g. Skyward"
                  />
                  <span className="text-danger">{err.project_name}</span>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h6 className="form-section-title">
              URLs by stage
              <span className="form-section-hint">
                Optional — add the frontend, admin, docs, etc. URL for development / staging / production
              </span>
            </h6>
            <StageUrlsEditor
              value={data.urls}
              onChange={(urls) => onChange("urls", urls)}
              err={err.urls}
            />
          </div>

          <div className="form-section">
            <ServersPanel
              value={data.servers || []}
              onChange={(servers) => onChange("servers", servers)}
            />
          </div>

          <div className="form-section">
            <h6 className="form-section-title">
              Members
              <span className="form-section-hint">
                Only assigned members see this project
              </span>
            </h6>
            <Form.Group className="mb-0">
              <MultiSelect
                options={
                  allEmployees.length
                    ? allEmployees.map((ele) => ({
                        label:
                          [ele.firstname, ele.lastname]
                            .filter(Boolean)
                            .join(" ") ||
                          ele.name ||
                          ele.email,
                        value: ele._id,
                        _id: ele._id,
                      }))
                    : []
                }
                value={data.members || []}
                onChange={(v) => onChange("members", v)}
                labelledBy="Select"
              />
              <span className="text-danger d-block">{err.members}</span>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="btn-primary-solid" onClick={submit}>
            {data.id ? "Save changes" : "Create project"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/**
 * Open a SSH terminal tab for one of the project's servers. Same URL
 * shape ProjectServersPage uses — keeping a single pattern means the
 * terminal page works regardless of which entry point launched it.
 */
function openProjectTerminal(projectId, server) {
  const qs = new URLSearchParams({
    label: server.label || "",
    host: `${server.username}@${server.host}:${server.port}`,
  });
  window.open(`/terminal/${projectId}/${server._id}?${qs.toString()}`, "_blank", "noopener,noreferrer");
}

/**
 * Open the file manager for one of the project's servers. Mirrors
 * ProjectServersPage.openFiles.
 */
function openProjectFiles(projectId, projectName, server) {
  const qs = new URLSearchParams({
    label: `${projectName || "Project"} · ${server.label || ""}`,
  });
  window.open(`/files/${projectId}/${server._id}?${qs.toString()}`, "_blank", "noopener,noreferrer");
}

/**
 * One inline action button for a project card. Behavior depends on
 * how many servers the project has:
 *   - 0 servers → button is disabled with a helpful title.
 *   - 1 server  → click runs `onPick(theOnlyServer)` directly.
 *   - 2+ servers → button is a split dropdown; the main click picks
 *                  the FIRST server (predictable default), the chevron
 *                  reveals the rest. Keeps the common single-server
 *                  case to one click while still handling fleets.
 */
function ServerActionButton({ proj, label, title, variant = "light", iconOnly = false, onPick }) {
  const servers = Array.isArray(proj.servers) ? proj.servers : [];
  const cls = iconOnly ? "pc-icon-btn" : "";
  if (servers.length === 0) {
    return (
      <Button size="sm" variant={variant} className={cls} disabled title="This project has no servers yet" aria-label={iconOnly ? title : undefined}>
        {label}
      </Button>
    );
  }
  if (servers.length === 1) {
    return (
      <Button size="sm" variant={variant} className={cls} title={title} aria-label={iconOnly ? title : undefined} onClick={() => onPick(servers[0])}>
        {label}
      </Button>
    );
  }
  return (
    <Dropdown as="span">
      <Dropdown.Toggle as={Button} size="sm" variant={variant} className={cls} title={`${title} — pick a server`} aria-label={iconOnly ? title : undefined}>
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {servers.map((s) => (
          <Dropdown.Item key={s._id} onClick={() => onPick(s)}>
            <span style={{ fontWeight: 600 }}>{s.label}</span>
            <span className="text-muted small ms-2">{s.username}@{s.host}</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

// Stage palette — used by the stage chips at the top of every card and
// by the per-stage URL group labels. Keeps the visual language consistent
// across the page and other places that surface stages (DeployPage etc).
const STAGE_META = {
  production:  { label: "PROD",    color: "#b91c1c", bg: "#fee2e2" },
  staging:     { label: "STAGING", color: "#b45309", bg: "#fef3c7" },
  development: { label: "DEV",     color: "#2563eb", bg: "#dbeafe" },
};

function ProjectCard({ proj, isSuperAdmin, isAdmin, onEdit, onView, onLogs, onServers, onDeploy }) {
  const initials = (proj.project_name || "?").slice(0, 2).toUpperCase();
  const members = proj.memberDetails || [];
  const serverCount = proj.servers?.length || 0;

  // Group per-stage URLs once so the body just iterates the result.
  // Legacy flat fields are appended under "development" since they
  // predate the stages model.
  const urlsByStage = { production: [], staging: [], development: [] };
  for (const u of (proj.urls || [])) {
    if (!u?.url) continue;
    const stage = String(u.stage || "development").toLowerCase();
    if (!urlsByStage[stage]) urlsByStage[stage] = [];
    urlsByStage[stage].push({ id: u._id || u.url, label: u.label || "URL", url: u.url });
  }
  if (proj.web_url) urlsByStage.development.push({ id: `legacy-web-${proj._id}`, label: "Web", url: proj.web_url });
  if (proj.admin_url) urlsByStage.development.push({ id: `legacy-admin-${proj._id}`, label: "Admin", url: proj.admin_url });
  for (const a of ((proj.apis && proj.apis.length) ? proj.apis : (proj.api_url ? [{ _id: "legacy-api", label: "API", url: proj.api_url }] : []))) {
    if (a?.url) urlsByStage.development.push({ id: a._id || `api-${a.url}`, label: a.label || "API", url: a.url });
  }
  const hasAnyUrls = Object.values(urlsByStage).some((arr) => arr.length > 0);
  const stagesWithUrls = ["production", "staging", "development"].filter((s) => urlsByStage[s].length > 0);

  return (
    <div className="project-card">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="pc-head">
        <div className="pc-icon">{initials}</div>
        <div className="pc-title">
          <div className="pc-name" title={proj.project_name}>{proj.project_name}</div>
          <div className="pc-meta">
            <span className="d-inline-flex align-items-center gap-1">
              <Users size={11} /> {members.length} member{members.length === 1 ? "" : "s"}
            </span>
            {serverCount > 0 && (
              <span className="d-inline-flex align-items-center gap-1 ms-2">
                <Server size={11} /> {serverCount} server{serverCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
        {stagesWithUrls.length > 0 && (
          <div className="pc-stages">
            {stagesWithUrls.map((s) => {
              const m = STAGE_META[s];
              return (
                <span key={s} className="pc-stage-chip" style={{ background: m.bg, color: m.color }}>
                  {m.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* ── URL catalogue ──────────────────────────────────────── */}
      {hasAnyUrls ? (
        <div className="pc-urls">
          {["production", "staging", "development"].map((stage) => {
            const list = urlsByStage[stage];
            if (!list.length) return null;
            const m = STAGE_META[stage];
            return (
              <div key={stage} className="pc-url-group">
                <div className="pc-url-stage" style={{ color: m.color }}>{m.label}</div>
                {list.map((u) => (
                  <UrlRow key={u.id} label={u.label} url={u.url} />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pc-empty">No URLs configured</div>
      )}

      {/* ── Members ────────────────────────────────────────────── */}
      {members.length > 0 && (
        <div className="pc-members">
          {members.slice(0, 6).map((m, i) => {
            const full = `${m.firstname || ""} ${m.lastname || ""}`.trim() || m.email || "?";
            const init = (m.firstname?.[0] || m.email?.[0] || "?").toUpperCase()
                       + (m.lastname?.[0] || "").toUpperCase();
            return (
              <div
                key={m._id || i}
                className="pc-member"
                title={`${full}${m.email ? ` · ${m.email}` : ""}`}
              >
                {init.slice(0, 2)}
              </div>
            );
          })}
          {members.length > 6 && (
            <div className="pc-member more">+{members.length - 6}</div>
          )}
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────
          Icon-only toolbar. Tooltips on every button surface the
          action name on hover. The primary "Open" button is the
          same size as the others but red-filled to mark it as the
          headline action. Admin/super_admin actions collapse into
          the trailing overflow menu so the toolbar stays one row. */}
      <div className="pc-actions">
        <Button
          size="sm"
          className="pc-icon-btn pc-icon-btn--primary"
          onClick={onView}
          title="Open project"
          aria-label="Open project"
        >
          <ExternalLink size={14} />
        </Button>
        <ServerActionButton
          proj={proj}
          label={<Terminal size={14} />}
          title="Open SSH terminal"
          iconOnly
          onPick={(s) => openProjectTerminal(proj._id, s)}
        />
        <ServerActionButton
          proj={proj}
          label={<Folder size={14} />}
          title="Open file manager"
          iconOnly
          onPick={(s) => openProjectFiles(proj._id, proj.project_name, s)}
        />
        <Button
          size="sm"
          variant="light"
          className="pc-icon-btn"
          onClick={onDeploy}
          title="Deploy"
          aria-label="Deploy"
        >
          <Rocket size={14} />
        </Button>
        {(isAdmin || isSuperAdmin) && (
          <Dropdown align="end" className="pc-overflow ms-auto">
            <Dropdown.Toggle
              as={Button}
              size="sm"
              variant="light"
              className="pc-icon-btn"
              title="More actions"
              aria-label="More actions"
            >
              <MoreHorizontal size={14} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {/* Admin and super_admin both reach these — the API gates
                  project create/update/delete on isAdmin, so the UI must
                  not be stricter than the route. */}
              {isAdmin && (
                <>
                  <Dropdown.Item onClick={onServers}>
                    <Server size={13} className="me-2" />
                    Servers{serverCount ? ` (${serverCount})` : ""}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={onEdit}>
                    <Pencil size={13} className="me-2" /> Edit project
                  </Dropdown.Item>
                  <Dropdown.Item onClick={onLogs}>
                    <FileText size={13} className="me-2" /> Logs
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

/**
 * One URL row inside the per-stage group. Hover reveals copy + open
 * actions; clicking the label opens the URL. Truncates the URL with
 * ellipsis so a long host doesn't break the card width.
 */
function UrlRow({ label, url }) {
  if (!url) return null;
  const host = (() => {
    try { return new URL(url.startsWith("http") ? url : `https://${url}`).host; }
    catch { return url; }
  })();
  const copy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { navigator.clipboard?.writeText(url); } catch { /* ignore */ }
  };
  return (
    <a
      className="pc-url"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
    >
      <span className="pc-url-label">{label}</span>
      <span className="pc-url-host">{host}</span>
      <button
        type="button"
        className="pc-url-copy"
        onClick={copy}
        title="Copy URL"
        aria-label="Copy URL"
      >
        <CopyIcon size={11} />
      </button>
    </a>
  );
}

/**
 * Per-stage URL editor — each row is { label, url, stage }. Rows are
 * grouped visually by stage so the operator can scan "what's the staging
 * frontend?" at a glance. Empty rows (blank label AND blank url) are
 * preserved while editing but stripped on submit by the parent, so the
 * UX of leaving a half-typed row doesn't accidentally save garbage.
 */
function StageUrlsEditor({ value, onChange, err }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) =>
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const add = (stage) =>
    onChange([...rows, { label: "", url: "", stage }]);
  const remove = (i) =>
    onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      {PROJECT_STAGES.map((s) => {
        const stageRows = rows
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => (r.stage || "development") === s.value);
        return (
          <div key={s.value} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <strong style={{ color: s.color }}>
                <span
                  className="badge me-2"
                  style={{ background: s.bg, color: s.color, fontWeight: 600 }}
                >
                  {s.label}
                </span>
                <small className="text-muted">
                  {stageRows.length} URL{stageRows.length === 1 ? "" : "s"}
                </small>
              </strong>
              <Button
                size="sm"
                variant="link"
                className="p-0"
                onClick={() => add(s.value)}
              >
                + Add URL
              </Button>
            </div>
            {stageRows.length === 0 ? (
              <div className="small text-muted ms-1">No URLs for {s.label}.</div>
            ) : (
              stageRows.map(({ r, i }) => (
                <div key={i} className="d-flex gap-2 mb-1 align-items-center flex-wrap">
                  <Form.Control
                    size="sm"
                    placeholder="Label (e.g. Frontend, Admin, Docs)"
                    value={r.label}
                    onChange={(e) => update(i, { label: e.target.value })}
                    style={{ maxWidth: 220 }}
                  />
                  <Form.Control
                    size="sm"
                    placeholder="https://…"
                    value={r.url}
                    onChange={(e) => update(i, { url: e.target.value })}
                    style={{ fontFamily: "ui-monospace, monospace" }}
                  />
                  <Form.Select
                    size="sm"
                    value={r.stage || "development"}
                    onChange={(e) => update(i, { stage: e.target.value })}
                    style={{ maxWidth: 140 }}
                    title="Change the stage this URL belongs to"
                  >
                    {PROJECT_STAGES.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                  {/* Public bypass: when on, the portal forward-auth
                      gate returns 200 for this URL without any login.
                      Use sparingly — health pages, public docs, etc. */}
                  <Form.Check
                    type="switch"
                    id={`url-public-${i}`}
                    label={
                      <span
                        title="When ON, this URL bypasses the portal login gate (anyone can access)"
                        className="d-inline-flex align-items-center gap-1"
                      >
                        <Globe size={13} /> Public
                      </span>
                    }
                    checked={!!r.is_public}
                    onChange={(e) => update(i, { is_public: e.target.checked })}
                  />
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => remove(i)}
                    title="Remove"
                    aria-label="Remove URL"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))
            )}
          </div>
        );
      })}
      {err && <div className="text-danger small">{err}</div>}
    </div>
  );
}

export default Projects;
